import React from 'react';
import './ApiDoc.css';

function Endpoint({ method, path, summary, children }) {
  return (
    <div className="api-endpoint">
      <div className="api-endpoint-header">
        <span className={`api-method api-method-${method.toLowerCase()}`}>{method}</span>
        <code className="api-path">{path}</code>
      </div>
      <div className="api-endpoint-body">
        <p className="api-endpoint-summary">{summary}</p>
        {children}
      </div>
    </div>
  );
}

function ApiDoc() {
  return (
    <div className="api-doc">
      <div className="api-doc-header">
        <h1>VitalEngine API</h1>
        <p className="api-doc-subtitle">
          The VitalEngine API will eventually replace Salesforce as the source of truth for facility
          hierarchy and customer metadata, and will expose near real-time reporting data that today
          flows through the nightly reporting DB.
        </p>
        <div className="api-doc-status">
          <span className="api-status-pill">Draft · content to be filled in</span>
        </div>
      </div>

      <section className="api-section">
        <h2>Overview</h2>
        <p>
          This is a placeholder for the VitalEngine API documentation. As the API comes online we
          will document each endpoint here so VitalData (and other tools) can consume it directly
          instead of going through the reporting DB or Salesforce.
        </p>
        <div className="api-callout">
          <strong>Goal:</strong> Replace both the nightly reporting DB and the Salesforce facility
          hierarchy with a single VitalEngine API as the system of record.
        </div>
      </section>

      <section className="api-section">
        <h2>Authentication</h2>
        <p>
          Planned: OAuth2 client-credentials flow issued per integration. VitalData's backend will
          hold the client secret in AWS Secrets Manager and exchange it for a short-lived bearer
          token on the backend — never in the browser.
        </p>
        <pre className="api-code">{`Authorization: Bearer <access_token>
Content-Type: application/json`}</pre>
      </section>

      <section className="api-section">
        <h2>Hierarchy</h2>
        <p>
          Replaces the Salesforce <code>IDN → Division → Facility</code> tree. Exact shape TBD;
          expected to include soft-delete, effective dates, and multi-parent support so a facility
          can belong to more than one grouping (e.g. both a division and a service line).
        </p>

        <Endpoint
          method="GET"
          path="/v1/hierarchy"
          summary="Returns the full current hierarchy as a nested tree."
        >
          <div className="api-placeholder-note">
            Response schema to be documented when the endpoint is stable.
          </div>
        </Endpoint>

        <Endpoint
          method="GET"
          path="/v1/facilities/{id}"
          summary="Fetch a single facility with its ancestors and siblings."
        >
          <div className="api-placeholder-note">TBD</div>
        </Endpoint>
      </section>

      <section className="api-section">
        <h2>Reporting data</h2>
        <p>
          Near real-time counts and events for referrals, messages, VRounds, logins, and studies.
          The plan is to mirror the <code>rpt_*</code> semantics today but with push-based refresh
          so VitalData doesn't need the nightly incremental refresh.
        </p>

        <Endpoint
          method="GET"
          path="/v1/referrals"
          summary="List referrals with filters for facility, date range, and status."
        >
          <div className="api-placeholder-note">TBD</div>
        </Endpoint>

        <Endpoint
          method="GET"
          path="/v1/messages"
          summary="List chat / vmail / referral messages."
        >
          <div className="api-placeholder-note">TBD</div>
        </Endpoint>
      </section>

      <section className="api-section">
        <h2>Webhooks</h2>
        <p>
          Future: subscribe to events (new referral, new message, account group change) so VitalData
          can update its local cache incrementally instead of polling.
        </p>
      </section>

      <section className="api-section">
        <h2>Open questions</h2>
        <ul className="api-checklist">
          <li>Who owns the API spec (engineering, data platform, product)?</li>
          <li>Do we commit to OpenAPI 3.1 or a GraphQL schema?</li>
          <li>PHI handling — does the API return raw PHI and rely on field-level auth, or does it return anonymized data by default?</li>
          <li>SLA for reporting-data freshness once we move off the nightly DB.</li>
        </ul>
      </section>
    </div>
  );
}

export default ApiDoc;
